import { ReactNode, useEffect, useState } from 'react';
import { getAllStudio, getGames } from '../../../api';
import { fetchAdminEnvList } from '../../../api/admin/game';
import { CommonSelect } from '../../../components/select';
import { wrapRequestArea } from '../../../lib/http/lib';
import { IEffectiveEnvItem } from '../../../model/api-modules';
import { renderEnvTypeTag } from '../../lib';
import styles from './index.module.scss';
import { DeleteOutlined } from '@ant-design/icons';
import classNames from 'classnames';

interface IProps {
  site?: string;
  value?: IEffectiveEnvItem[];
  onChange?: (value?: IEffectiveEnvItem[]) => void;
}

interface IOption<T> {
  label: ReactNode;
  value: T;
  disabled?: boolean;
}

interface IEnvSelectorInfo extends IEffectiveEnvItem {
  envOptions?: IOption<number>[];
}

export type EnvSelectInfos = IEnvSelectorInfo[];

export const EnvSelector = (props: IProps) => {
  const { value, onChange, site } = props;
  const [studioOptions, setStudioOptions] = useState<IOption<string>[]>([]);
  const [gameOptions, setGameOptions] = useState<IOption<string>[]>([]);
  const [selectInfos, setSelectInfos] = useState<EnvSelectInfos>(
    value || [{ studioID: '', gameID: '', envID: 0, envOptions: [] }]
  );

  useEffect(() => {
    initStudioOptions();
  }, [site]);

  const checkDisableEnvOption = (infos: EnvSelectInfos) => {
    const allEnvs = infos.map((item) => item.envID);
    const newInfos = infos.map((selectItem) => {
      return {
        ...selectItem,
        envOptions: selectItem.envOptions?.map((option) => {
          return {
            ...option,
            disabled:
              allEnvs.includes(option.value) &&
              option.value !== selectItem.envID,
          };
        }),
      };
    });
    setSelectInfos(newInfos);
  };

  const initStudioOptions = async () => {
    if (!site) {
      return;
    }
    const studios = await wrapRequestArea(getAllStudio, site);
    if (!studios) {
      return;
    }
    setStudioOptions(
      studios.map((studio) => {
        return {
          label: studio.Name,
          value: studio.StudioID,
        };
      })
    );
  };

  const initGameOptions = async (studioID: string) => {
    const games = await wrapRequestArea(() => getGames(studioID), site);
    setGameOptions(
      games.map((game) => {
        return {
          label: game.Name,
          value: game.GameID,
        };
      })
    );
  };

  const changeStudio = (studioID: string) => {
    const newSelectInfos = [...selectInfos].map((item) => {
      return { ...item, studioID, gameID: '', envID: 0, envOptions: [] };
    });
    setSelectInfos(newSelectInfos);
    initGameOptions(studioID);
  };

  const changeGame = async (info: IEnvSelectorInfo, gameID: string) => {
    let newSelectInfos = [...selectInfos].map((item) => {
      return {
        ...item,
        gameID: item === info ? gameID : item.gameID,
        envID: item === info ? 0 : item.envID,
      };
    });
    setSelectInfos(newSelectInfos);
    if (info.gameID) {
      onChange?.(undefined);
    }
    const envs = await wrapRequestArea(() => fetchAdminEnvList(gameID), site);
    if (!envs) {
      return;
    }
    newSelectInfos = [...selectInfos].map((item) => {
      return {
        ...item,
        gameID: item === info ? gameID : item.gameID,
        envID: item === info ? 0 : item.envID,
        envOptions:
          item === info
            ? envs.map((env) => {
                return {
                  label: (
                    <>
                      <span style={{ marginRight: 5 }}>
                        {renderEnvTypeTag(env.Type)}
                      </span>
                      {env.Name}
                    </>
                  ),
                  value: env.id,
                };
              })
            : item.envOptions,
      };
    });
    setSelectInfos(newSelectInfos);
    checkDisableEnvOption(newSelectInfos);
  };

  const changeEnv = (info: IEnvSelectorInfo, envID: number) => {
    const newSelectInfos = [...selectInfos].map((selectItem) => {
      const newEnvId = selectItem === info ? envID : selectItem.envID;
      return {
        ...selectItem,
        envID: newEnvId,
      };
    });
    setSelectInfos(newSelectInfos);
    checkDisableEnvOption(newSelectInfos);

    if (newSelectInfos.every((item) => item.envID)) {
      onChange?.(
        newSelectInfos.map((item) => {
          return {
            studioID: item.studioID,
            gameID: item.gameID,
            envID: item.envID,
          };
        })
      );
    }
  };

  const addEnv = () => {
    setSelectInfos([
      ...selectInfos,
      { studioID: selectInfos?.[0]?.studioID, gameID: '', envID: 0 },
    ]);
  };
  const deleteEnv = (info: IEnvSelectorInfo) => {
    const newInfos = [...selectInfos].filter((item) => item !== info);
    setSelectInfos(newInfos);
    checkDisableEnvOption(newInfos);
  };

  const hasDelBtn = selectInfos.length > 1;
  const hasAddBtn = selectInfos.length < 3;

  return (
    <div className={styles.envSelector}>
      <div
        className={classNames(styles.envSelectTable, {
          [styles.envSelectTable3Column]: !hasDelBtn,
        })}
      >
        {selectInfos.map((info) => {
          return (
            <>
              <CommonSelect
                placeholder="请选择工作室"
                value={info.studioID}
                options={studioOptions}
                onChange={(val) => changeStudio(val)}
                showSearch
              ></CommonSelect>
              <CommonSelect
                value={info.gameID}
                placeholder="请选择游戏"
                options={gameOptions}
                showSearch
                onChange={(val) => changeGame(info, val)}
              ></CommonSelect>
              <CommonSelect
                value={info.envID || ''}
                placeholder="请选择环境"
                showSearch
                options={info.envOptions}
                onChange={(val) => changeEnv(info, val)}
              ></CommonSelect>
              {hasDelBtn && (
                <DeleteOutlined
                  className={styles.delIcon}
                  onClick={() => deleteEnv(info)}
                />
              )}
            </>
          );
        })}
      </div>
      {hasAddBtn && (
        <div onClick={addEnv} className={styles.addEnvBtn}>
          + 添加环境
        </div>
      )}
    </div>
  );
};
